import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useUpdateEmailTemplateMutation } from '@/redux/email-templates/emailTemplatesApi';
import type { CatalogueEntry } from '@/redux/email-templates/emailTemplatesApi';
import type { RootState } from '@/redux/common/store';

interface Props {
  entry: CatalogueEntry;
  onClose: () => void;
}

const TemplateEditorModal: React.FC<Props> = ({ entry, onClose }) => {
  const { t } = useTranslation();
  const roles = useSelector((state: RootState) => state.sessionState.roles);
  const isPlatformAdmin = roles.includes('platform_admin') || roles.includes('platform_support');

  const effective = entry.effective_value as { subject?: string; content?: string };
  const [subject, setSubject] = useState(effective.subject ?? '');
  const [content, setContent] = useState(effective.content ?? '');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [error, setError] = useState('');

  const [updateConfig, { isLoading }] = useUpdateEmailTemplateMutation();

  useEffect(() => {
    setSubject(effective.subject ?? '');
    setContent(effective.content ?? '');
    setError('');
  }, [entry.key]);

  const isBase = entry.key === 'email_template.base';
  const hasTenantOverride = Object.keys(entry.tenant_value ?? {}).length > 0;
  const variables: string[] = entry.variables ?? [];

  const handleSave = async () => {
    setError('');
    const value: Record<string, string> = { content };
    if (!isBase) value.subject = subject;
    try {
      await updateConfig({ key: entry.key, value }).unwrap();
      onClose();
    } catch {
      setError(t('common.failedToSave'));
    }
  };

  const previewHtml = content.replace(
    /\{\{[\w]+\}\}/g,
    (m) =>
      `<span style="background:#ECD6FF;color:#4E38B5;padding:0 3px;border-radius:3px;font-weight:600">${m}</span>`,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-ui-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-ui-border2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-bold text-ui-text">{entry.name ?? entry.key}</h2>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isPlatformAdmin ? 'bg-purple-100 text-brand-dark' : 'bg-blue-100 text-blue-700'}`}>
                {isPlatformAdmin ? 'System layer' : 'Tenant override'}
              </span>
              {hasTenantOverride && isPlatformAdmin && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  Tenant override active
                </span>
              )}
            </div>
            <p className="text-xs text-ui-text2">
              <code className="bg-ui-subtle px-1.5 py-0.5 rounded text-xs">{entry.key}</code>
              {variables.length > 0 && (
                <span className="ml-3">
                  Variables:{' '}
                  {variables.map((v) => (
                    <code key={v} className="bg-brand-light text-brand-dark px-1.5 py-0.5 rounded text-xs mr-1">
                      {`{{${v}}}`}
                    </code>
                  ))}
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-ui-text3 hover:text-ui-text2 p-1 mt-0.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Subject */}
        {!isBase && (
          <div className="px-6 pt-4">
            <label className="block text-sm font-medium text-ui-text mb-1">Subject Line</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-ui-border bg-ui-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/30 focus:border-brand-dark"
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-ui-border2 mt-4 px-6">
          {(['edit', 'preview'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition capitalize ${activeTab === tab ? 'border-brand-dark text-brand-dark' : 'border-transparent text-ui-text2 hover:text-ui-text'}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Editor / Preview */}
        <div className="flex-1 overflow-hidden px-6 py-4 min-h-0">
          {activeTab === 'edit' ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-full min-h-[300px] border border-ui-border bg-ui-input rounded-lg px-3 py-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-brand-dark/30 focus:border-brand-dark"
              spellCheck={false}
            />
          ) : (
            <iframe
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
              title="Email preview"
              className="w-full min-h-[300px] h-full border border-ui-border bg-ui-raised rounded-lg"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-ui-border2">
          {error ? <p className="text-red-500 text-sm">{error}</p> : <span />}
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-ui-text2 border border-ui-border rounded-lg hover:bg-ui-hover transition">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-5 py-2 text-sm font-semibold bg-brand-dark text-white rounded-lg hover:bg-[#3d2b9c] transition disabled:opacity-50"
            >
              {isLoading ? 'Saving…' : `Save ${isPlatformAdmin ? 'System Default' : 'Tenant Override'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditorModal;
